import * as THREE from 'three/webgpu';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { MeasureEntityRef } from '../types/measure-types';
import { cloneMeasureEntityRef } from '../types/measure-types';
import { isMeasurableObject } from './MeasureHitResolver';

/**
 * 测量选中引导结果。
 */
export interface MeasureSelectionBootstrapResult {
    /** 引导后的测量选中实体列表。 */
    entities: MeasureEntityRef[];
    /** 引导后的拾取历史（与 entities 顺序一致）。 */
    pickHistory: MeasureEntityRef[];
}

/**
 * 从底层选中命中引导测量初始选中集。
 *
 * 设计说明：
 * 1. 对齐 SolidWorks：进入 Measure 前已选中的可测量拓扑自动填入测量选中列表；
 * 2. 保持底层选中顺序，按 objectId 去重；
 * 3. worldPickPoint 优先复用真实射线命中点；程序化伪命中点按几何类型回退。
 *
 * @param selectedPicks 底层 `EntitySelectionManager.getSelectedPicks()` 快照。
 * @returns 可写入 `MeasureController` 的引导结果。
 */
export function bootstrapMeasureSelectionFromUnderlying(
    selectedPicks: readonly THREE.Intersection<THREE.Object3D>[],
): MeasureSelectionBootstrapResult {
    const entities: MeasureEntityRef[] = [];
    const seenObjectIds = new Set<string>();

    for (const pick of selectedPicks) {
        if (!isMeasurableObject(pick.object)) {
            continue;
        }

        if (seenObjectIds.has(pick.object.uuid)) {
            continue;
        }

        const entityRef = resolveMeasureEntityRefFromProgrammaticPick(pick);
        if (!entityRef) {
            continue;
        }

        seenObjectIds.add(entityRef.objectId);
        entities.push(entityRef);
    }

    return {
        entities,
        pickHistory: entities.map((entity) => cloneMeasureEntityRef(entity)),
    };
}

/**
 * 将程序化选中命中转换为测量实体引用。
 *
 * 设计说明：
 * 1. 供 bootstrap 与设计树/程序化选中复用，统一可测量判定与拾取点回退；
 * 2. 视口真实射线命中仍由 `resolveMeasureEntityRef` 处理，以保留精确 pick.point。
 *
 * @param pick 程序化选中命中。
 * @returns 测量实体引用；不可测量时返回 null。
 */
export function resolveMeasureEntityRefFromProgrammaticPick(
    pick: THREE.Intersection<THREE.Object3D>,
): MeasureEntityRef | null {
    if (!isMeasurableObject(pick.object)) {
        return null;
    }

    return {
        objectId: pick.object.uuid,
        worldPickPoint: resolveWorldPickPoint(pick),
    };
}

/**
 * 解析测量引导用的世界拾取点。
 *
 * 设计说明：
 * 1. 真实鼠标射线命中时优先复用 `pick.point`，保证锚点落在用户点击位置；
 * 2. 程序化选中 / Ctrl 保留选中会写入 `distance=0 + getWorldPosition()` 伪命中点，
 *    该点通常不在面/边几何上，必须回退到几何参考点，否则浮窗锚点会偏移到奇怪位置。
 *
 * @param pick 底层选中命中。
 * @returns 世界坐标拾取点。
 */
function resolveWorldPickPoint(
    pick: THREE.Intersection<THREE.Object3D>,
): THREE.Vector3 {
    if (!isSyntheticUnderlyingSelectionPickPoint(pick)) {
        return pick.point.clone();
    }

    return resolveFallbackWorldPickPoint(pick.object);
}

/**
 * 判断底层选中命中是否为程序化合成的伪射线点。
 *
 * @param pick 底层选中命中。
 * @returns 伪命中点时返回 true。
 */
function isSyntheticUnderlyingSelectionPickPoint(
    pick: THREE.Intersection<THREE.Object3D>,
): boolean {
    if (!isFiniteVector3(pick.point)) {
        return true;
    }

    // 真实射线命中会携带沿射线方向的正距离；程序化选中固定为 0。
    if (pick.distance !== 0) {
        return false;
    }

    const objectWorldPosition = pick.object.getWorldPosition(new THREE.Vector3());
    return pick.point.distanceToSquared(objectWorldPosition) <= 1e-12;
}

/**
 * 判断向量三分量是否均为有限数值。
 *
 * @param vector 待判断向量。
 * @returns 是否为有效有限向量。
 */
function isFiniteVector3(vector: THREE.Vector3): boolean {
    return Number.isFinite(vector.x)
        && Number.isFinite(vector.y)
        && Number.isFinite(vector.z);
}

/**
 * 按几何类型回退世界拾取点。
 *
 * 说明：与 `EntitySelectionManager.selectEntitiesByObjectIds` 的程序化选中语义保持一致，
 * 在缺少真实射线命中点时为锚点/标注提供稳定参考位置。
 *
 * @param object 场景对象。
 * @returns 世界坐标参考点。
 */
function resolveFallbackWorldPickPoint(object: THREE.Object3D): THREE.Vector3 {
    if (isFeatureFace(object)) {
        return object.getWorldPoint().clone();
    }

    if (isFeaturePoint(object) || isDatumPoint(object)) {
        return object.getWorldPoint().clone();
    }

    if (isFeatureLine(object)) {
        const midpoint = object.getWorldMidpoint();
        if (midpoint) {
            return midpoint.clone();
        }
    }

    if (isDatumAxis(object)) {
        const midpoint = object.getWorldMidpoint();
        if (midpoint) {
            return midpoint.clone();
        }
        return object.originWorld.clone();
    }

    if (isDatumPlaneObject(object)) {
        const planePoint = resolveDatumPlaneReferencePoint(object);
        if (planePoint) {
            return planePoint;
        }
    }

    return object.getWorldPosition(new THREE.Vector3());
}

/**
 * 解析基准面层级中的平面参考点（世界坐标）。
 *
 * @param planeObject 基准面对象。
 * @returns 平面参考点；无法解析时返回 null。
 */
function resolveDatumPlaneReferencePoint(planeObject: THREE.Object3D): THREE.Vector3 | null {
    let referencePlane: THREE.Plane | null = null;

    planeObject.traverse((childObject) => {
        if (referencePlane) {
            return;
        }

        const candidate = Reflect.get(childObject, 'referencePlane');
        if (candidate instanceof THREE.Plane) {
            referencePlane = candidate;
        }
    });

    if (!referencePlane) {
        return null;
    }

    const point = referencePlane.coplanarPoint(new THREE.Vector3());
    planeObject.localToWorld(point);
    return point;
}
